import { AfterViewInit, Component, ViewChild, signal, inject, Input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';

import { IMAGE_URLS } from 'src/app/core/constants/image-source';
import { Product } from '../../models/product.model';
import { ProductsApiService } from '../../services/products-api.service';
import { Action, GoogleAnalyticsService } from 'src/app/core/services/analytics/google-analytics.service';
import { ChatGptShimmerComponent } from "./chat-gpt-shimmer/chat-gpt-shimmer.component";
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';

enum ChatGptOptionType {
  Compare = 'compare',
  Reviews = 'reviews',
  Highlights = 'highlights',
  ReasonToBuy = 'reason_to_buy',
  UseCase = 'use_case',
  LearnMore = 'learn_more'
}

interface ChatOption {
  iconUrl: string;
  title: string;
  caption: string;
  type: ChatGptOptionType;
}

@Component({
  standalone: true,
  selector: 'webstore-chat-gpt',
  templateUrl: './chat-gpt.component.html',
  styleUrls: ['./chat-gpt.component.scss'],
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ChatGptShimmerComponent]
})
export class ChatGptComponent implements AfterViewInit {
  private productsApiService = inject(ProductsApiService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);

  @ViewChild('inputBox') inputBox!: HTMLInputElement;

  IMAGE_URLS = IMAGE_URLS;

  @Input() set productInput(product: Product) {
    this._product.set(product);
  }

  private _product = signal<Product>({} as Product);
  readonly product = this._product.asReadonly();

  readonly productName = computed(() => this.product()?.name || '');

  readonly updatedProductName = computed(() => {
    const name = this.productName();
    const closingBracketIndex = name.indexOf(')');
    return name.startsWith('(') ? name.slice(closingBracketIndex + 1, name.length) : name;
  });

  ChatGptOptionType = ChatGptOptionType;

  private _chatGptOptions = signal<ChatOption[]>([
    {
      iconUrl:
        'https://assets.hyperinvento.com/www_imaginext_com/assets/imaginext-assets/images/shop-cart-icon.svg',
      title: 'Why should i buy this?',
      caption: `Discover this product's exceptional qualities before buying.`,
      type: ChatGptOptionType.ReasonToBuy
    },
    {
      iconUrl:
        'https://assets.hyperinvento.com/www_imaginext_com/assets/imaginext-assets/images/product-compare-icon.svg',
      title: 'Compare with other product',
      caption: 'Comparing this product to any others that are comparable.',
      type: ChatGptOptionType.Compare
    },
    {
      iconUrl:
        'https://assets.hyperinvento.com/www_imaginext_com/assets/imaginext-assets/images/product-review-icon.svg',
      title: 'Get most popular reviews',
      caption: 'Find product reviews for a better experience.',
      type: ChatGptOptionType.Reviews
    },
    {
      iconUrl:
        'https://assets.hyperinvento.com/www_imaginext_com/assets/imaginext-assets/images/use-case-icon.svg',
      title: 'Where I can use this?',
      caption: 'Understand the usability of the product.',
      type: ChatGptOptionType.UseCase
    },
    {
      iconUrl:
        'https://assets.hyperinvento.com/www_imaginext_com/assets/imaginext-assets/images/product-highlights.svg',
      title: 'Get main highlights',
      caption: 'Know more about the main highlights of the product.',
      type: ChatGptOptionType.Highlights
    },
    {
      iconUrl:
        'https://assets.hyperinvento.com/www_imaginext_com/assets/imaginext-assets/images/learn-more-icon.svg',
      title: 'Learn more about this product',
      caption: 'Discover more information about this product.',
      type: ChatGptOptionType.LearnMore
    }
  ]);
  readonly chatGptOptions = this._chatGptOptions.asReadonly();

  private _isExpanded = signal<boolean>(false);
  readonly isExpanded = this._isExpanded.asReadonly();

  private _chatGptResponse = signal<string>('');
  readonly chatGptResponse = this._chatGptResponse.asReadonly();

  private _selectedChatGptOption = signal<ChatOption | null>(null);
  readonly selectedChatGptOption = this._selectedChatGptOption.asReadonly();

  private _hasSelectedOption = signal<boolean>(false);
  readonly hasSelectedOption = this._hasSelectedOption.asReadonly();

  private _isOptionSelected = signal<boolean>(false);
  readonly isOptionSelected = this._isOptionSelected.asReadonly();

  private _chatButtonLoading = signal<boolean>(false);
  readonly chatButtonLoading = this._chatButtonLoading.asReadonly();

  private _error = signal<boolean>(false);
  readonly error = this._error.asReadonly();

  readonly chatGptOptionsToShow = computed(() => {
    const options = this.chatGptOptions();
    return options.slice(0, this.isExpanded() ? options.length : 4);
  });

  chatInput = new FormControl<string>('');

  ngAfterViewInit(): void {
    this.inputBox?.focus();
  }

  toggleViewMore() {
    this._isExpanded.update(expanded => !expanded);
  }

  selectChatGptOption(chatOption: ChatOption) {
    if (chatOption) {
      this._selectedChatGptOption.set(chatOption);
      this._hasSelectedOption.set(true);
      this._isOptionSelected.set(true);
    }
    this._chatGptResponse.set('');
    this._error.set(false);
    if (chatOption.type !== ChatGptOptionType.Compare) {
      this.chatGptPrompt(chatOption);
    }
  }

  chatGptPrompt(chatOption: ChatOption) {
    if (!this.chatButtonLoading()) {
      if (chatOption.type === ChatGptOptionType.Compare) {
        if (this.chatInput.value) {
          this.getChatGptResponse(chatOption.type);
        }
      } else {
        this.getChatGptResponse(chatOption.type);
      }
    }
  }

  async getChatGptResponse(chatOptionType: ChatGptOptionType) {
    const promptText = this.chatGptPromptOptions(chatOptionType);
    this._chatButtonLoading.set(true);
    this.googleAnalyticsService.injectScript({ action: Action.AIProductDetails });

    try {
      const chatGptResponse = await this.productsApiService.askChatGpt(promptText, this.product().id).toPromise();
      this._chatButtonLoading.set(false);
      if (!chatGptResponse?.body) {
        this._error.set(true);
        this._chatGptResponse.set('');
      } else {
        this._chatGptResponse.set(chatGptResponse.body);
      }
    } catch (error) {
      this._chatButtonLoading.set(false);
      this._error.set(true);
      this._chatGptResponse.set('');
    }
  }

  chatGptPromptOptions(option: ChatGptOptionType) {
    const chatGptPromptOptions = {
      [ChatGptOptionType.Compare]: this.updatedProductName() + ' comparison with ' + this.chatInput.value,
      [ChatGptOptionType.ReasonToBuy]: 'why should I buy ' + this.updatedProductName(),
      [ChatGptOptionType.Reviews]: 'Find reviews for ' + this.updatedProductName(),
      [ChatGptOptionType.UseCase]: 'What are the use case of ' + this.updatedProductName(),
      [ChatGptOptionType.Highlights]: this.updatedProductName() + ' key highlights',
      [ChatGptOptionType.LearnMore]: 'Get more information about ' + this.updatedProductName(),
    };
    return chatGptPromptOptions[option];
  }

  resetChatGptResponse() {
    this._chatGptResponse.set('');
    this._chatButtonLoading.set(false);
    this.chatInput.reset();
    this._error.set(false);
    this._hasSelectedOption.set(false);
    this._isOptionSelected.set(false);
    this._selectedChatGptOption.set(null);
  }
}

